import { isActiveProcessingJob, type ProcessingJob } from "../backgroundActivity/types";

/**
 * Live generation detail for the Feature Studio work product cards.
 *
 * The cards' own job poll (/api/jobprocessor/status) only carries queue
 * lifecycle — no percentage and no section message — so a generating card would
 * otherwise sit on its seeded "Starting generation..." text for the whole run.
 * The processing-jobs feed that backs the Background Activity flyout already
 * carries the processor's live detail ("Processing section 4 of 6", 50%), so the
 * cards read from that same source rather than a second, parallel one.
 */
export interface LiveWorkProductProgress {
    /** Processor message, e.g. "Processing section 4 of 6". */
    message: string | null;
    /** Percentage 0-100, or null when the processor has not reported one. */
    progress: number | null;
}

const clampPercentage = (value: unknown): number | null => {
    if (typeof value !== "number" || !Number.isFinite(value)) return null;
    return Math.max(0, Math.min(100, Math.round(value)));
};

/**
 * Index the active work-product generation jobs by work product id.
 *
 * Work product ids are only unique within a product, so jobs carrying a product
 * id that disagrees with the current product are skipped. Jobs without a product
 * id are kept — the feed is already scoped to the caller's authorized products.
 */
export function selectLiveWorkProductProgress(
    jobs: readonly ProcessingJob[] | null | undefined,
    currentProductId?: string | number | null
): Map<string, LiveWorkProductProgress> {
    const byWorkProduct = new Map<string, LiveWorkProductProgress>();
    if (!jobs?.length) return byWorkProduct;

    for (const job of jobs) {
        if (job.kind !== "work_product") continue;
        if (job.workProductId == null || job.workProductId === "") continue;
        if (!isActiveProcessingJob(job)) continue;
        if (currentProductId != null && job.productId != null && String(job.productId) !== String(currentProductId)) {
            continue;
        }

        const message = typeof job.message === "string" && job.message.trim() ? job.message.trim() : null;
        byWorkProduct.set(String(job.workProductId), {
            message,
            progress: clampPercentage(job.progress)
        });
    }

    return byWorkProduct;
}
